import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { Message, MessageContent as MessageLayout, MessageFooter, MessageHeader } from '@/components/ui/message'
import { Bubble, BubbleContent } from '@/components/ui/bubble'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { memo, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { sendMessageFeedback } from 'api/feedback'
import { readChatMessage } from 'api/voice'
import { settingsSelectors } from 'store/settings'
import { socketSingleton } from 'services/ws'
import { ChatMessage, FeedbackEnum } from 'types/chat-message'
import { ChatRoleEnum } from 'types/chat'
import { MessageContent } from 'components/messages/MessageContent'
import { formatISODate } from 'utils/dates'
import { cn } from '@/lib/utils'
import { useIsAudioLoading, useIsAudioPlaying } from 'hooks/useAudioPlayback'
import { audioPlayer } from 'services/audio-player'
import { analytics, ANALYTICS_EVENTS } from 'services/analytics'

type TextMessageProps = {
  messageItem: ChatMessage
  text: string
  /** Shown in the header above the bubble (bot name or operator name). */
  senderName?: string
  isFirstMessage?: boolean
}

const TextMessageComponent = ({ text, senderName, messageItem, isFirstMessage }: TextMessageProps) => {
  const { t } = useTranslation()
  const [feedback, setFeedback] = useState<FeedbackEnum | undefined>(messageItem.feedback)

  const onFeedback = useCallback(
    async (value: FeedbackEnum) => {
      setFeedback(value)
      await sendMessageFeedback({ messageId: messageItem._id, feedback: value })
    },
    [messageItem._id],
  )

  const showActions = useMemo(() => {
    return messageItem.chatSenderRole === ChatRoleEnum.BOT && !isFirstMessage
  }, [messageItem, isFirstMessage])

  const isSpeaking = useIsAudioPlaying(messageItem._id)
  // "TTS being synthesized": derived from the player (expected but not yet playing).
  const listenLoading = useIsAudioLoading(messageItem._id)
  const voiceEnabled = useSelector(settingsSelectors.voiceEnabled)
  const showVoice = voiceEnabled && messageItem.chatSenderRole === ChatRoleEnum.BOT

  const onListen = useCallback(() => {
    analytics.track(ANALYTICS_EVENTS.MESSAGE_AUDIO_PLAYED)
    // Inside the click gesture: earns the autoplay grant. expect() silences whatever else
    // is talking (one voice at a time). Replay audio arrives on the WS room named after
    // the message (left again by the end_stream_audio handler).
    audioPlayer.unlock()
    audioPlayer.expect(messageItem._id)
    socketSingleton.join(messageItem._id)
    readChatMessage(messageItem._id).catch((e) => {
      socketSingleton.leave(messageItem._id)
      audioPlayer.stop(messageItem._id) // un-expect: clears the loading state too
      console.error({ e })
    })
  }, [messageItem._id])

  return (
    <Message align="start">
      <MessageLayout className="gap-0.5">
        {senderName && (
          <MessageHeader className="gap-1.5 font-semibold text-foreground">
            <span
              aria-hidden
              className={cn(
                'h-0.5 w-4 shrink-0 rounded-full',
                messageItem.chatSenderRole === ChatRoleEnum.BOT
                  ? 'bg-linear-to-r from-primary to-primary/15'
                  : 'bg-border',
              )}
            />
            <span className="truncate">{senderName}</span>
          </MessageHeader>
        )}
        {/* Width-fit to the text, but a chart (ChartCitation) takes the full allowed width: on both the
            bubble and its (also width-fit) content box, or the chart still shrinks to the text. */}
        <Bubble
          variant="muted"
          className="max-w-[90%] in-data-[surface=page]:max-w-[95%] has-data-[slot=chart-citation]:w-full"
        >
          <BubbleContent dir="auto" className="has-data-[slot=chart-citation]:w-full">
            <MessageContent text={text} />
          </BubbleContent>
        </Bubble>
        <MessageFooter className="gap-2">
          <span>{formatISODate(messageItem.createdAt)}</span>
          {showActions && (
            <span className="flex items-center">
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={t('goodResponse')}
                title={t('goodResponse')}
                aria-pressed={feedback === FeedbackEnum.GOOD}
                className={feedback === FeedbackEnum.GOOD ? 'text-foreground' : ''}
                onClick={() => onFeedback(FeedbackEnum.GOOD).catch(console.error)}
              >
                <OdinIcon aria-hidden icon={[feedback === FeedbackEnum.GOOD ? 'fas' : 'far', 'thumbs-up']} />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={t('badResponse')}
                title={t('badResponse')}
                aria-pressed={feedback === FeedbackEnum.BAD}
                className={feedback === FeedbackEnum.BAD ? 'text-foreground' : ''}
                onClick={() => onFeedback(FeedbackEnum.BAD).catch(console.error)}
              >
                <OdinIcon aria-hidden icon={[feedback === FeedbackEnum.BAD ? 'fas' : 'far', 'thumbs-down']} />
              </Button>
            </span>
          )}
          {showVoice && (
            <span className="flex items-center gap-1 text-primary">
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={isSpeaking || listenLoading ? t('voiceStopPlayback') : t('voiceListen')}
                title={isSpeaking || listenLoading ? t('voiceStopPlayback') : t('voiceListen')}
                onClick={() => (isSpeaking || listenLoading ? audioPlayer.stop(messageItem._id) : onListen())}
              >
                {listenLoading ? (
                  <Spinner aria-hidden />
                ) : (
                  <OdinIcon aria-hidden icon={isSpeaking ? ['fas', 'stop'] : ['far', 'volume']} />
                )}
              </Button>
              {isSpeaking && (
                <>
                  <span aria-hidden className="eq-bars">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="sr-only" role="status">
                    {t('voicePlaying')}
                  </span>
                </>
              )}
            </span>
          )}
        </MessageFooter>
      </MessageLayout>
    </Message>
  )
}

export const TextMessage = memo(TextMessageComponent)
